import { useState, useEffect, useCallback, useRef } from 'react';
import { applyDocumentSeo, SEO_KEYWORDS_PRIMARY } from '../components/SeoHead';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Scale, LayoutDashboard, Users, LogOut, Download, Search, RefreshCw, Car, Stethoscope, Trash2, Copy, Paperclip, ShieldCheck, Menu, X, Phone, Settings2, Mail, Wallet, CreditCard, Briefcase, Archive, TrendingUp, BarChart3, Calculator, HeartPulse, Flag, KeyRound, MailX } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiUrl, getApiBase, leadsFromApiPayload, LEADS_PAGE_SIZE, formatApiErrorDetail } from '@/lib/apiBase';
import { escapeHTMLWithBreaks } from '@/lib/escapeHtml';
import { LeadReplayHistory } from '@/lib/leadReplayFormat';
import { AdminAnalyticsPanel } from '@/components/admin/AdminAnalyticsPanel';
import { AdminFinancialSummaryPanel } from '@/components/admin/AdminFinancialSummaryPanel';
import { DannoBiologicoCalculator } from '@/components/DannoBiologicoCalculator';
import { DisputeLeadModal } from '@/components/DisputeLeadModal';
import { DeclineLeadModal } from '@/components/DeclineLeadModal';
import { canDisputeLead } from '@/lib/leadDispute';
import { canDeclineLead } from '@/lib/leadDecline';

const FILTER_OPTIONS = [
  { value: 'tutti', label: 'Tutti i Lead', icon: Users },
  { value: 'stradale', label: 'Stradale', icon: Car },
  { value: 'malasanita', label: 'Malasanità', icon: Stethoscope }
];

const QUICK_MODE_STORAGE_KEY = 'leadlegale_dashboard_quick_mode';
const ADMIN_SECTION_STORAGE_KEY = 'leadlegale_admin_section';
const LAWYER_VIEW_STORAGE_KEY = 'leadlegale_lawyer_view';

const ADMIN_SECTIONS = [
  { id: 'overview', label: 'Panoramica', icon: LayoutDashboard },
  { id: 'analytics', label: 'Statistiche', icon: BarChart3 },
  { id: 'financial', label: 'Resoconto Calcoli', icon: Calculator },
  { id: 'calculator', label: 'Danno Biologico', icon: HeartPulse },
  { id: 'lawyers', label: 'Gestione Avvocati', icon: Briefcase },
  { id: 'leads', label: 'Archivio Lead', icon: Archive },
];

const ADMIN_SECTION_TITLES = {
  overview: 'Panoramica',
  analytics: 'Statistiche',
  financial: 'Resoconto Calcoli',
  calculator: 'Calcolatore Danno Biologico',
  lawyers: 'Gestione Avvocati',
  leads: 'Archivio Lead Globale',
};

const PLAN_LABELS = {
  starter: 'Starter',
  business: 'Business',
  pro: 'Pro',
};

function generateStrongPassword(length = 12) {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%^&*';
  const pick = (alphabet) => alphabet[Math.floor(Math.random() * alphabet.length)];
  const chars = [pick(upper), pick(lower), pick(digits), pick(special)];
  const all = `${upper}${lower}${digits}${special}`;
  while (chars.length < length) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function formatEuroAmount(value) {
  return Number(value ?? 0).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function AdminFinancialCards({
  totalCredits,
  activeLawyers,
  totalLawyers,
  totalRevenue,
}) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-6"
      data-testid="admin-platform-stats"
    >
      <div
        className="rounded-lg p-4 md:p-5"
        style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.28)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>
              Totale Crediti Attivi
            </p>
            <p className="text-2xl md:text-3xl font-semibold mt-2" style={{ color: '#D4AF37' }}>
              {formatEuroAmount(totalCredits)} €
            </p>
            <p className="text-xs mt-2" style={{ color: '#A0ABC0' }}>
              Crediti residui nei portafogli avvocati
            </p>
          </div>
          <div
            className="rounded-lg p-2.5 shrink-0"
            style={{ backgroundColor: 'rgba(212, 175, 55, 0.12)', border: '1px solid rgba(212, 175, 55, 0.25)' }}
          >
            <Wallet className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
          </div>
        </div>
      </div>
      <div
        className="rounded-lg p-4 md:p-5"
        style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>
              Avvocati Attivi
            </p>
            <p className="text-2xl md:text-3xl font-semibold mt-2" style={{ color: '#F8F9FA' }}>
              {activeLawyers}
            </p>
            <p className="text-xs mt-2" style={{ color: '#A0ABC0' }}>
              {totalLawyers - activeLawyers > 0
                ? `${totalLawyers - activeLawyers} sospesi su ${totalLawyers} totali`
                : `${totalLawyers} partner registrati`}
            </p>
          </div>
          <div
            className="rounded-lg p-2.5 shrink-0"
            style={{ backgroundColor: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.25)' }}
          >
            <Users className="w-5 h-5" style={{ color: '#10B981' }} strokeWidth={1.5} />
          </div>
        </div>
      </div>
      <div
        className="rounded-lg p-4 md:p-5 sm:col-span-2 lg:col-span-1"
        style={{ backgroundColor: '#112240', border: '1px solid rgba(59, 130, 246, 0.35)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>
              Totale Incassato
            </p>
            <p className="text-2xl md:text-3xl font-semibold mt-2" style={{ color: '#60A5FA' }}>
              {formatEuroAmount(totalRevenue)} €
            </p>
            <p className="text-xs mt-2" style={{ color: '#A0ABC0' }}>
              Fatturato storico cumulativo (ricariche registrate)
            </p>
          </div>
          <div
            className="rounded-lg p-2.5 shrink-0"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.25)' }}
          >
            <TrendingUp className="w-5 h-5" style={{ color: '#60A5FA' }} strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </div>
  );
}

const PLAN_LEADS_INCLUDED = {
  starter: 0,
  business: 4,
  pro: 12,
};

function CreditsWalletCard({ user, onManagePlan, onRequestTopup, requestMsg, isRequesting, className = '', layout = 'compact' }) {
  const credits = Number(user?.credits ?? 0);
  const leadsRemaining = Number(user?.leads_included_remaining ?? 0);
  const plan = String(user?.subscription_plan || 'starter');
  const planTotal = Number(
    user?.leads_included_total ?? PLAN_LEADS_INCLUDED[plan] ?? PLAN_LEADS_INCLUDED.starter
  );
  const isActive = user?.is_active !== false;
  const creditsFrozen = user?.credits_frozen === true || String(user?.subscription_status || '') === 'expired';
  const isWide = layout === 'wide';

  return (
    <div
      className={`rounded-xl ${isWide ? 'p-4 md:p-5' : 'p-3'} ${className}`}
      style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.35)' }}
      data-testid="credits-wallet-card"
    >
      {!isActive && !creditsFrozen && (
        <div
          className="mb-3 rounded-lg px-3 py-2 text-xs font-medium"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.45)', color: '#FCA5A5' }}
          data-testid="lawyer-suspended-banner"
        >
          Sospeso dall&apos;assegnazione automatica per esaurimento risorse. Effettua una ricarica.
        </div>
      )}
      {creditsFrozen && (
        <div
          className="mb-3 rounded-lg px-3 py-2 text-xs font-medium"
          style={{ backgroundColor: 'rgba(212, 175, 55, 0.12)', border: '1px solid rgba(212, 175, 55, 0.4)', color: '#FCD34D' }}
          data-testid="credits-frozen-banner"
        >
          Crediti congelati: abbonamento non attivo. Il saldo non viene azzerato e si sblocca al rinnovo.
        </div>
      )}

      <div className={`${isWide ? 'flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4' : ''}`}>
        <div className={isWide ? 'flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4' : ''}>
          <div className={isWide ? '' : 'mb-3'}>
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#D4AF37' }}>
                Portafoglio Partner
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#A0ABC0' }}>Piano attivo</p>
            <p className="text-lg font-semibold" style={{ color: '#F8F9FA' }}>
              Piano: {PLAN_LABELS[plan] || plan}
            </p>
          </div>

          <div>
            <p className="text-[11px]" style={{ color: '#A0ABC0' }}>Lead inclusi rimanenti</p>
            <p className="text-lg font-semibold" style={{ color: '#60A5FA' }}>
              {leadsRemaining} / {planTotal} lead inclusi rimanenti
            </p>
          </div>

          <div>
            <p className="text-[11px]" style={{ color: '#A0ABC0' }}>
              {creditsFrozen ? 'Credito extra congelato' : 'Credito extra disponibile'}
            </p>
            <p className="text-lg font-bold" style={{ color: '#D4AF37' }}>
              {credits.toFixed(2)} €
            </p>
          </div>
        </div>

        <div className={`flex flex-col gap-2 ${isWide ? 'lg:w-64 shrink-0' : ''}`}>
          {onRequestTopup && (
            <Button
              type="button"
              onClick={onRequestTopup}
              disabled={isRequesting}
              className="w-full text-xs sm:text-sm"
              style={{ backgroundColor: '#D4AF37', color: '#0A192F', fontWeight: 600 }}
              data-testid="request-topup-btn"
            >
              <TrendingUp className="w-4 h-4 mr-1.5" />
              {isRequesting ? 'Invio richiesta...' : 'Richiedi Ricarica / Upgrade'}
            </Button>
          )}
          <Button
            type="button"
            onClick={onManagePlan}
            className="w-full text-xs sm:text-sm"
            style={{ backgroundColor: '#1e293b', color: '#F8F9FA', border: '1px solid rgba(212, 175, 55, 0.4)' }}
            data-testid="manage-plan-btn"
          >
            <CreditCard className="w-4 h-4 mr-1.5" />
            Dettagli piano
          </Button>
          {requestMsg && (
            <p className="text-[11px] text-center" style={{ color: requestMsg.includes('errore') || requestMsg.includes('Errore') ? '#FCA5A5' : '#22C55E' }}>
              {requestMsg}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'tutti', label: 'Tutti gli Stati' },
  { value: 'nuovo', label: 'Nuovo' },
  { value: 'in_contatto', label: 'In Contatto' },
  { value: 'in_attesa_doc', label: 'In Attesa Doc' },
  { value: 'pratica_aperta', label: 'Pratica Aperta' },
  { value: 'contestato', label: 'Contestato' },
  { value: 'declinato', label: 'Declinato' },
  { value: 'non_fattibile_chiuso', label: 'Non Fattibile / Chiuso' },
];

function LawyerAssignedLeadStatus({ lawyer }) {
  const inGestione = Number(lawyer?.assigned_leads_in_gestione ?? 0);
  const chiusi = Number(lawyer?.assigned_leads_chiusi ?? 0);
  const total = Number(lawyer?.assigned_leads_total ?? 0);

  if (total === 0) {
    return (
      <span className="text-xs" style={{ color: '#64748B' }}>
        Nessun lead assegnato
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs" style={{ color: '#F8F9FA' }}>
        {inGestione} in gestione / {chiusi} chiusi
      </span>
      <div className="flex flex-wrap gap-1">
        {inGestione > 0 && (
          <span
            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: 'rgba(96, 165, 250, 0.18)', color: '#60A5FA' }}
          >
            {inGestione} attivi
          </span>
        )}
        {chiusi > 0 && (
          <span
            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: 'rgba(148, 163, 184, 0.18)', color: '#94A3B8' }}
          >
            {chiusi} chiusi
          </span>
        )}
      </div>
    </div>
  );
}

function dedupeLeadsById(leads) {
  const seen = new Set();
  const unique = [];
  for (const lead of Array.isArray(leads) ? leads : []) {
    const id = String(lead?.id ?? lead?._id ?? '').trim();
    const fallbackKey = [
      String(lead?.email || '').toLowerCase(),
      String(lead?.telefono || ''),
      String(lead?.created_at || ''),
      String(lead?.nome || '').toLowerCase(),
      String(lead?.cognome || '').toLowerCase(),
    ].join('|');
    const key = id || fallbackKey;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(lead);
  }
  return unique;
}

function sortLeadsByPriority(leads) {
  return dedupeLeadsById(leads).sort((a, b) => {
    const sa = Number.isFinite(a?.score) ? a.score : Number(a?.score || 0);
    const sb = Number.isFinite(b?.score) ? b.score : Number(b?.score || 0);
    if (sa !== sb) return sb - sa;

    const categoryRank = { ORO: 0, ARGENTO: 1, BRONZO: 2 };
    const ca = categoryRank[String(a?.category || '').toUpperCase()] ?? 99;
    const cb = categoryRank[String(b?.category || '').toUpperCase()] ?? 99;
    if (ca !== cb) return ca - cb;

    const da = Date.parse(a?.created_at || '') || 0;
    const db = Date.parse(b?.created_at || '') || 0;
    return db - da;
  });
}

function LeadCard({
  lead,
  onOpenDetails,
  renderStatusBadge,
  renderCategoryBadge,
  isAssignedToCurrentLawyer,
  quickMode,
  canContest,
  onContestLead,
  canDecline,
  onDeclineLead,
}) {
  const scoreVal = Number.isFinite(lead.score) ? lead.score : 0;

  if (quickMode) {
    return (
      <div
        className="w-full text-left rounded-xl p-4 transition-colors"
        style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.10)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={`score-badge shrink-0 ${scoreVal >= 80 ? 'score-top' : 'score-normal'}`}>
            {scoreVal}
          </div>
          <button
            type="button"
            className="flex-1 min-w-0 text-left rounded-lg px-1 py-1 -m-1 transition-colors hover:bg-white/5"
            style={{ color: '#F8F9FA' }}
            onClick={() => onOpenDetails(lead)}
          >
            <span className="text-lg font-semibold leading-tight block truncate">
              {lead.nome} {lead.cognome}
            </span>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <a
            href={`tel:${lead.telefono || ''}`}
            className="inline-flex items-center justify-center gap-2 rounded-xl py-4 text-base font-semibold shadow-md"
            style={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}
          >
            <Phone className="w-5 h-5" />
            Chiama
          </a>
          <a
            href={lead.email ? `mailto:${lead.email}` : '#'}
            className="inline-flex items-center justify-center gap-2 rounded-xl py-4 text-base font-semibold border-2"
            style={{
              backgroundColor: 'rgba(10, 25, 47, 0.6)',
              borderColor: '#D4AF37',
              color: '#F8F9FA',
            }}
          >
            <Mail className="w-5 h-5" style={{ color: '#D4AF37' }} />
            Email
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetails(lead)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpenDetails(lead);
      }}
      className="w-full text-left rounded-xl p-4 transition-colors"
      style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.10)' }}
    >
      <div className="lead-row">
        <div className={`score-badge ${scoreVal >= 80 ? 'score-top' : 'score-normal'}`}>
          {scoreVal}
        </div>

        <div style={{ flexGrow: 1 }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#F8F9FA' }}>
            {lead.nome} {lead.cognome}
          </div>
          <div className="lead-mini-info">
            {lead.gravita || ''}
          </div>
        </div>

        <div style={{ color: '#A0ABC0', fontSize: '12px' }}>
          {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '—'}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {renderCategoryBadge(lead.category)}
        </div>
        <div>{renderStatusBadge(lead.stato || 'nuovo')}</div>
      </div>

      <p className="text-xs mt-2" style={{ color: '#A0ABC0' }}>{lead.email || '-'}</p>

      <div className="mt-3 text-xs" style={{ color: '#A0ABC0' }}>
        {isAssignedToCurrentLawyer(lead) ? 'Assegnato a te' : 'Non assegnato'}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <a
          href={`tel:${lead.telefono || ''}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold"
          style={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}
        >
          <Phone className="w-4 h-4" />
          Chiama
        </a>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails(lead);
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold"
          style={{ backgroundColor: '#1E293B', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.12)' }}
        >
          <Settings2 className="w-4 h-4" />
          Gestisci
        </button>
      </div>
      {canContest ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onContestLead(lead);
          }}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold"
          style={{ backgroundColor: 'rgba(212, 175, 55, 0.12)', color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.4)' }}
          data-testid={`contest-lead-mobile-${lead.id}`}
        >
          <Flag className="w-4 h-4" />
          Contesta Lead (Garanzia 24h)
        </button>
      ) : null}
      {canDecline ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeclineLead(lead);
          }}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.35)' }}
          data-testid={`decline-lead-mobile-${lead.id}`}
        >
          <MailX className="w-4 h-4" />
          Declina Pratica
        </button>
      ) : null}
    </div>
  );
}

export function DashboardPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('tutti');
  const [categoryFilter, setCategoryFilter] = useState('tutti');
  const [statusFilter, setStatusFilter] = useState('tutti');
  const [searchQuery, setSearchQuery] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvExporting, setIsCsvExporting] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [disputeLead, setDisputeLead] = useState(null);
  const [isDisputing, setIsDisputing] = useState(false);
  const [disputeError, setDisputeError] = useState('');
  const [declineLead, setDeclineLead] = useState(null);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineError, setDeclineError] = useState('');
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [copiedLeadId, setCopiedLeadId] = useState('');
  const [allLeadsStats, setAllLeadsStats] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const location = useLocation();
  const [assigningLeadId, setAssigningLeadId] = useState('');
  const [assignmentSavedLeadId, setAssignmentSavedLeadId] = useState('');
  const [newLawyerName, setNewLawyerName] = useState('');
  const [newLawyerEmail, setNewLawyerEmail] = useState('');
  const [newLawyerPassword, setNewLawyerPassword] = useState('');
  const [newLawyerCategory, setNewLawyerCategory] = useState('all');
  const [isCreatingLawyer, setIsCreatingLawyer] = useState(false);
  const [deletingLawyerId, setDeletingLawyerId] = useState('');
  const [lawyerMsg, setLawyerMsg] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  const [leadsDebugMsg, setLeadsDebugMsg] = useState('');
  const [leadActionMsg, setLeadActionMsg] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isAdminTopupOpen, setIsAdminTopupOpen] = useState(false);
  const [topupLawyer, setTopupLawyer] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupPlan, setTopupPlan] = useState('');
  const [topupMsg, setTopupMsg] = useState('');
  const [isTopupSaving, setIsTopupSaving] = useState(false);
  const [resetLawyer, setResetLawyer] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [financialData, setFinancialData] = useState(null);
  const [isLoadingFinancial, setIsLoadingFinancial] = useState(false);
  const [topupRequestMsg, setTopupRequestMsg] = useState('');
  const [isRequestingTopup, setIsRequestingTopup] = useState(false);
  const [adminSection, setAdminSection] = useState(() => {
    try {
      const saved = localStorage.getItem(ADMIN_SECTION_STORAGE_KEY);
      if (['overview', 'analytics', 'financial', 'calculator', 'lawyers', 'leads'].includes(saved)) return saved;
    } catch {
      /* ignore */
    }
    return 'overview';
  });
  const [quickMode, setQuickMode] = useState(() => {
    try {
      return localStorage.getItem(QUICK_MODE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [lawyerView, setLawyerView] = useState(() => {
    try {
      const saved = localStorage.getItem(LAWYER_VIEW_STORAGE_KEY);
      if (saved === 'calculator' || saved === 'leads') return saved;
    } catch {
      /* ignore */
    }
    return 'leads';
  });
  const leadsFetchGen = useRef(0);
  const effectiveRole = String(user?.role || '').toLowerCase();
  const effectiveUserId = String(user?.id || '');
  const effectiveUserEmail = String(user?.email || '').toLowerCase();
  const effectiveUsername = String(user?.username || '').toLowerCase();
  const effectiveUserName = String(user?.name || '').toLowerCase();
  const isLawyerLike = effectiveRole === 'lawyer' || effectiveRole === 'partner';
  const isAdmin = effectiveRole === 'admin';
  const buildAuthHeaders = (extra = {}) => ({
    Authorization: `Bearer ${localStorage.getItem('leadlegale_token') || ''}`,
    ...extra,
  });
  const formatLeadActionError = (err) => {
    const status = err?.response?.status;
    const detail = formatApiErrorDetail(err?.response?.data?.detail);
    if (status === 401) {
      return detail || 'Sessione scaduta. Effettua di nuovo l\'accesso.';
    }
    if (status === 403) {
      return detail || 'Non sei autorizzato a eseguire questa operazione.';
    }
    if (status === 409) {
      return detail || 'Il lead non è più assegnato a te. Aggiorna la pagina.';
    }
    return detail || 'Operazione non riuscita. Riprova.';
  };
  const isAssignedToCurrentLawyer = (lead) => {
    const assigned = String(lead?.assigned_to || '').toLowerCase();
    return (
      assigned === effectiveUserId
      || assigned === effectiveUserEmail
      || assigned === effectiveUsername
      || assigned === effectiveUserName
    );
  };

  const handleQuickModeChange = (checked) => {
    const next = Boolean(checked);
    setQuickMode(next);
    try {
      localStorage.setItem(QUICK_MODE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const handleAdminSectionChange = (section) => {
    setAdminSection(section);
    try {
      localStorage.setItem(ADMIN_SECTION_STORAGE_KEY, section);
    } catch {
      /* ignore */
    }
    setMobileMenuOpen(false);
  };

  const handleLawyerViewChange = (view) => {
    setLawyerView(view);
    try {
      localStorage.setItem(LAWYER_VIEW_STORAGE_KEY, view);
    } catch {
      /* ignore */
    }
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (isLawyerLike && typeof checkAuth === 'function') {
      checkAuth();
    }
  }, [isLawyerLike, user?.id, checkAuth]);

  useEffect(() => {
    applyDocumentSeo({
      title: 'Dashboard gestione lead | Lead Legale',
      description:
        'Area riservata per studi legali: gestione lead infortunistica stradale, risarcimento danni e consulenza legale. Accesso professionisti.',
      keywords: SEO_KEYWORDS_PRIMARY,
      path: '/dashboard',
      noindex: true,
    });
  }, []);

  const fetchLeads = useCallback(async () => {
    const gen = ++leadsFetchGen.current;
    setIsLoading(true);
    setLeadsDebugMsg('');
    try {
      const ts = Date.now();
      const requestConfig = {
        headers: {
          ...buildAuthHeaders({
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            'x-user-id': effectiveUserId || String(user?.username || ''),
            'x-user-role': effectiveRole || String(user?.role || ''),
          }),
        },
      };
      const aggregated = [];
      let skip = 0;
      let total = 0;
      for (;;) {
        const { data } = await axios.get(apiUrl('/api/leads'), {
          ...requestConfig,
          params: { limit: LEADS_PAGE_SIZE, skip, t: ts },
        });
        if (gen !== leadsFetchGen.current) return;
        const page = leadsFromApiPayload(data);
        total = typeof data?.total === 'number' ? data.total : page.length;
        aggregated.push(...page);
        if (!data?.has_more || page.length === 0 || aggregated.length >= total) break;
        skip += page.length;
      }

      const raw = aggregated;
      const unique = dedupeLeadsById(raw);
      const forDisplay =
        activeFilter === 'tutti'
          ? unique
          : unique.filter(
              (lead) => String(lead?.tipo_incidente || '').toLowerCase() === activeFilter
            );
      setLeads(sortLeadsByPriority(forDisplay));

      const statsSource = isLawyerLike
        ? unique.filter((lead) => isAssignedToCurrentLawyer(lead))
        : unique;
      setAllLeadsStats(statsSource);

      if (forDisplay.length === 0) {
        if (unique.length === 0) {
          setLeadsDebugMsg('Lista vuota: nessun lead nel database (MongoDB).');
        } else if (activeFilter !== 'tutti') {
          setLeadsDebugMsg('Nessun lead per il filtro tipo selezionato.');
        } else if (isLawyerLike) {
          setLeadsDebugMsg('Nessun lead assegnato al tuo utente. Verifica assegnazione e user ID inviato.');
        }
      }
    } catch (err) {
      if (gen !== leadsFetchGen.current) return;
      const status = err?.response?.status;
      if (status === 401) {
        setLeadsDebugMsg('Errore 401 Unauthorized: autenticazione scaduta o token non valido.');
      } else {
        setLeadsDebugMsg('Errore caricamento lead: controlla connessione API o cache mobile.');
      }
      console.error('Error fetching leads:', err);
    } finally {
      if (gen === leadsFetchGen.current) {
        setIsLoading(false);
      }
    }
  }, [
    activeFilter,
    effectiveRole,
    effectiveUserId,
    effectiveUserEmail,
    effectiveUsername,
    isLawyerLike,
    user?.role,
    user?.username,
  ]);

  useEffect(() => {
    if (!user?.id && !user?.email) return;
    fetchLeads();
  }, [fetchLeads, user?.id, user?.email]);

  const fetchLawyers = useCallback(async () => {
    if (String(user?.role || '').toLowerCase() !== 'admin') return;
    try {
      const { data } = await axios.get(
        `${apiUrl('/api/users/lawyers')}?t=${new Date().getTime()}`,
        {
          headers: {
            ...buildAuthHeaders(),
            'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
            Pragma: 'no-cache',
            Expires: '0',
          },
        }
      );
      setLawyers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching lawyers:', err);
      setLawyers([]);
    }
  }, [user?.role, user?.id]);

  const fetchPlatformStats = useCallback(async () => {
    if (String(user?.role || '').toLowerCase() !== 'admin') return;
    try {
      const { data } = await axios.get(
        `${apiUrl('/api/admin/platform-stats')}?t=${Date.now()}`,
        {
          headers: {
            ...buildAuthHeaders(),
            'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
          },
        }
      );
      setPlatformStats(data);
    } catch (err) {
      console.error('Error fetching platform stats:', err);
    }
  }, [user?.role, user?.id]);

  const fetchAnalytics = useCallback(async () => {
    if (String(user?.role || '').toLowerCase() !== 'admin') return;
    setIsLoadingAnalytics(true);
    try {
      const { data } = await axios.get(
        `${apiUrl('/api/admin/analytics')}?t=${Date.now()}`,
        { headers: { ...buildAuthHeaders(), 'Cache-Control': 'no-store' } }
      );
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [user?.role, user?.id]);

  const fetchFinancialSummary = useCallback(async () => {
    if (String(user?.role || '').toLowerCase() !== 'admin') return;
    setIsLoadingFinancial(true);
    try {
      const { data } = await axios.get(
        `${apiUrl('/api/admin/financial-summary')}?t=${Date.now()}`,
        { headers: { ...buildAuthHeaders(), 'Cache-Control': 'no-store' } }
      );
      setFinancialData(data);
    } catch (err) {
      console.error('Error fetching financial summary:', err);
    } finally {
      setIsLoadingFinancial(false);
    }
  }, [user?.role, user?.id]);

  useEffect(() => {
    if (String(user?.role || '').toLowerCase() !== 'admin') return;
    fetchLawyers();
    fetchPlatformStats();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        fetchLawyers();
        fetchPlatformStats();
      }
    };
    const onFocus = () => {
      fetchLawyers();
      fetchPlatformStats();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.role, user?.id, location.pathname, fetchLawyers, fetchPlatformStats]);

  useEffect(() => {
    if (!isAdmin || adminSection !== 'analytics') return undefined;
    fetchAnalytics();
    const intervalId = window.setInterval(fetchAnalytics, 60000);
    return () => window.clearInterval(intervalId);
  }, [isAdmin, adminSection, fetchAnalytics]);

  useEffect(() => {
    if (!isAdmin || adminSection !== 'financial') return undefined;
    fetchFinancialSummary();
    const intervalId = window.setInterval(fetchFinancialSummary, 60000);
    return () => window.clearInterval(intervalId);
  }, [isAdmin, adminSection, fetchFinancialSummary]);

  useEffect(() => {
    // Filter leads by role + active UI filters (dedupe per ID univoco).
    let result = dedupeLeadsById(leads);
    if (isLawyerLike) {
      result = result.filter((lead) => isAssignedToCurrentLawyer(lead));
    }

    if (categoryFilter !== 'tutti') {
      result = result.filter((lead) => String(lead.category || '').toUpperCase() === categoryFilter);
    }
    if (statusFilter !== 'tutti') {
      result = result.filter((lead) => String(lead.stato || '') === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((lead) =>
        lead.nome.toLowerCase().includes(query) ||
        lead.cognome.toLowerCase().includes(query) ||
        lead.email.toLowerCase().includes(query) ||
        lead.luogo.toLowerCase().includes(query)
      );
    }

    setFilteredLeads(sortLeadsByPriority(result));
  }, [searchQuery, leads, categoryFilter, statusFilter, effectiveRole, effectiveUserId, isLawyerLike, user?.email, user?.username]);

  const openAdminTopup = (lawyer) => {
    setTopupLawyer(lawyer);
    setTopupAmount('');
    setTopupPlan('');
    setTopupMsg('');
    setIsAdminTopupOpen(true);
  };

  const openResetLawyerPassword = (lawyer) => {
    setResetLawyer(lawyer);
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetMsg('');
    setResetCopied(false);
  };

  const fillGeneratedResetPassword = () => {
    const generated = generateStrongPassword();
    setResetPassword(generated);
    setResetPasswordConfirm(generated);
    setResetCopied(false);
    setResetMsg('Password casuale generata. Copiala prima di confermare.');
  };

  const copyResetPassword = async () => {
    if (!resetPassword) return;
    try {
      await navigator.clipboard.writeText(resetPassword);
      setResetCopied(true);
      window.setTimeout(() => setResetCopied(false), 2000);
    } catch {
      setResetMsg('Impossibile copiare negli appunti.');
    }
  };

  const submitResetLawyerPassword = async () => {
    if (!resetLawyer?.id) return;
    const pwd = String(resetPassword || '').trim();
    const confirm = String(resetPasswordConfirm || '').trim();
    if (!pwd || !confirm) {
      setResetMsg('Inserisci e conferma la nuova password.');
      return;
    }
    if (pwd !== confirm) {
      setResetMsg('La conferma password non coincide.');
      return;
    }
    setIsResettingPassword(true);
    setResetMsg('');
    try {
      const { data } = await axios.post(
        apiUrl(`/api/admin/lawyers/${encodeURIComponent(resetLawyer.id)}/reset-password`),
        { new_password: pwd },
        { headers: buildAuthHeaders() }
      );
      setResetMsg(data?.message || 'Password aggiornata.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setResetMsg(typeof detail === 'string' ? detail : 'Errore durante il reset password.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const requestLawyerTopup = async () => {
    setIsRequestingTopup(true);
    setTopupRequestMsg('');
    try {
      const { data } = await axios.post(
        apiUrl('/api/users/request-topup'),
        {},
        { headers: buildAuthHeaders() }
      );
      setTopupRequestMsg(data?.message || 'Richiesta inviata all\'amministratore.');
    } catch (err) {
      setTopupRequestMsg(formatApiErrorDetail(err?.response?.data?.detail) || 'Errore durante l\'invio della richiesta.');
    } finally {
      setIsRequestingTopup(false);
    }
  };

  const walletCardProps = {
    user,
    onManagePlan: () => setIsPlanModalOpen(true),
    onRequestTopup: requestLawyerTopup,
    requestMsg: topupRequestMsg,
    isRequesting: isRequestingTopup,
  };

  const submitAdminTopup = async () => {
    if (!topupLawyer?.id) return;
    const amount = topupAmount === '' ? 0 : parseFloat(topupAmount);
    if (!topupPlan && (topupAmount === '' || Number.isNaN(amount))) {
      setTopupMsg('Inserisci un importo (anche negativo per scalare) o seleziona un piano');
      return;
    }
    setIsTopupSaving(true);
    setTopupMsg('');
    try {
      const payload = { lawyer_id: topupLawyer.id };
      if (topupAmount !== '' && !Number.isNaN(amount)) payload.amount = amount;
      if (topupPlan) payload.plan = topupPlan;
      await axios.post(apiUrl('/api/admin/topup'), payload, { headers: buildAuthHeaders() });
      setTopupMsg('Ricarica applicata con successo');
      await fetchLawyers();
      await fetchPlatformStats();
      setTimeout(() => {
        setIsAdminTopupOpen(false);
        setTopupMsg('');
        setTopupLawyer(null);
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setTopupMsg(typeof detail === 'string' ? detail : 'Errore durante la ricarica');
    } finally {
      setIsTopupSaving(false);
    }
  };

  const createLawyer = async () => {
    if (String(user?.role || '').toLowerCase() !== 'admin') return;
    setLawyerMsg('');
    setIsCreatingLawyer(true);
    try {
      const payload = {
        name: newLawyerName.trim(),
        email: newLawyerEmail.trim().toLowerCase(),
        password: newLawyerPassword,
        practice_category: newLawyerCategory,
      };
      const { data: created } = await axios.post(
        apiUrl('/api/users/lawyers'),
        payload,
        { headers: buildAuthHeaders() }
      );
      if (created?.id) {
        setLawyers((prev) => {
          const exists = prev.some((l) => String(l.id) === String(created.id));
          return exists ? prev : [...prev, created];
        });
      }
      await fetchLawyers();
      setNewLawyerName('');
      setNewLawyerEmail('');
      setNewLawyerPassword('');
      setNewLawyerCategory('all');
      setLawyerMsg('Partner creato con successo');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLawyerMsg(typeof detail === 'string' ? detail : 'Errore creazione avvocato');
    } finally {
      setIsCreatingLawyer(false);
    }
  };

  const deleteLawyer = async (lawyer) => {
    if (String(user?.role || '').toLowerCase() !== 'admin' || !lawyer?.id) return;
    const label = lawyer.name || lawyer.username || lawyer.email || 'questo avvocato';
    if (!window.confirm(`Sei sicuro di voler eliminare questo avvocato?\n\n${label}`)) return;

    const lawyerId = String(lawyer.id);
    setDeletingLawyerId(lawyerId);
    setLawyerMsg('');
    try {
      await axios.delete(apiUrl(`/api/users/lawyers/${encodeURIComponent(lawyerId)}`), {
        headers: buildAuthHeaders(),
      });
      setLawyers((prev) => prev.filter((l) => String(l.id) !== lawyerId));
      setLeads((prev) =>
        prev.map((lead) =>
          String(lead.assigned_to || '') === lawyerId ||
          String(lead.assigned_to || '').toLowerCase() === String(lawyer.email || '').toLowerCase() ||
          String(lead.assigned_to || '').toLowerCase() === String(lawyer.username || '').toLowerCase()
            ? { ...lead, assigned_to: null }
            : lead
        )
      );
      setLawyerMsg('Avvocato eliminato con successo');
      await fetchLawyers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLawyerMsg(typeof detail === 'string' ? detail : 'Errore eliminazione avvocato');
    } finally {
      setDeletingLawyerId('');
    }
  };

  const canContestLead = (lead) =>
    canDisputeLead(lead, {
      isAssigned: isAssignedToCurrentLawyer(lead),
      isAdmin,
    });

  const canDeclineAssignedLead = (lead) =>
    canDeclineLead(lead, {
      isAssigned: isAssignedToCurrentLawyer(lead),
      isAdmin,
    });

  const openDisputeLead = (lead) => {
    setDisputeError('');
    setDisputeLead(lead);
  };

  const submitLeadDispute = async ({ motivo, note }) => {
    if (!disputeLead?.id) return;
    setIsDisputing(true);
    setDisputeError('');
    try {
      const { data } = await axios.post(
        apiUrl(`/api/leads/${disputeLead.id}/dispute`),
        { motivo, note: note || '' },
        { headers: buildAuthHeaders() }
      );
      const nextStatus = data?.stato || 'contestato';
      const patchLead = (lead) =>
        lead.id === disputeLead.id
          ? {
              ...lead,
              stato: nextStatus,
              disputed: true,
              disputed_at: new Date().toISOString(),
              dispute_reason: motivo,
            }
          : lead;
      setLeads((prev) => sortLeadsByPriority(prev.map(patchLead)));
      setSelectedLead((prev) => (prev ? patchLead(prev) : prev));
      setLeadActionMsg(data?.message || 'Contestazione inviata.');
      setDisputeLead(null);
      if (typeof checkAuth === 'function') {
        await checkAuth({ silent: true });
      }
    } catch (err) {
      setDisputeError(formatLeadActionError(err));
    } finally {
      setIsDisputing(false);
    }
  };

  const openDeclineLead = (lead) => {
    setDeclineError('');
    setDeclineLead(lead);
  };

  const submitLeadDecline = async ({ custom_message }) => {
    if (!declineLead?.id) return;
    setIsDeclining(true);
    setDeclineError('');
    try {
      const { data } = await axios.post(
        apiUrl(`/api/leads/${declineLead.id}/decline`),
        { custom_message: custom_message || '' },
        { headers: buildAuthHeaders() }
      );
      const patchLead = (lead) =>
        lead.id === declineLead.id
          ? {
              ...lead,
              stato: data?.stato || 'declinato',
              declined_at: data?.declined_at || new Date().toISOString(),
              decline_message: custom_message || lead.decline_message,
            }
          : lead;
      setLeads((prev) => sortLeadsByPriority(prev.map(patchLead)));
      setSelectedLead((prev) => (prev ? patchLead(prev) : prev));
      setLeadActionMsg(data?.message || 'Pratica declinata. Email di cortesia in invio.');
      setDeclineLead(null);
      setIsDetailsOpen(false);
    } catch (err) {
      setDeclineError(formatLeadActionError(err));
    } finally {
      setIsDeclining(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setLeadActionMsg('');
    try {
      await axios.patch(
        apiUrl(`/api/leads/${leadId}/status`),
        { stato: newStatus },
        { headers: buildAuthHeaders() }
      );
      setLeads(prev => sortLeadsByPriority(prev.map(lead => 
        lead.id === leadId ? { ...lead, stato: newStatus } : lead
      )));
    } catch (err) {
      setLeadActionMsg(formatLeadActionError(err));
    }
  };

  const assignLead = async (leadId, lawyerId) => {
    setAssigningLeadId(leadId);
    try {
      const assignedValue = lawyerId || null;
      await axios.patch(
        apiUrl(`/api/assign-lead/${leadId}`),
        { lawyer_id: assignedValue },
        { headers: buildAuthHeaders() }
      );
      setLeads((prev) =>
        sortLeadsByPriority(prev.map((lead) => (lead.id === leadId ? { ...lead, assigned_to: assignedValue } : lead)))
      );
      setAssignmentSavedLeadId(leadId);
      window.setTimeout(() => setAssignmentSavedLeadId(''), 1600);
    } catch (err) {
      console.error('Error assigning lead:', err);
    } finally {
      setAssigningLeadId('');
    }
  };

  const getLawyerNameById = (lawyerId) => {
    if (!lawyerId) return 'Non assegnato';
    const found = lawyers.find((lawyer) => String(lawyer.id) === String(lawyerId));
    return found?.name || found?.username || String(lawyerId);
  };

  const deleteLead = async (leadId) => {
    const ok = window.confirm('Sei sicuro di voler eliminare questo contatto?');
    if (!ok) return;
    setLeadActionMsg('');
    try {
      await axios.delete(apiUrl(`/api/leads/${leadId}`), { headers: buildAuthHeaders() });
      setLeads(prev => sortLeadsByPriority(prev.filter(lead => lead.id !== leadId)));
      setFilteredLeads(prev => sortLeadsByPriority(prev.filter(lead => lead.id !== leadId)));
    } catch (err) {
      setLeadActionMsg(formatLeadActionError(err));
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = activeFilter !== 'tutti' ? { tipo: activeFilter } : {};
      const response = await axios.get(apiUrl('/api/leads/export'), {
        params,
        headers: buildAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting leads:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCsvExport = () => {
    setIsCsvExporting(true);
    try {
      const rows = filteredLeads.map((lead) => ({
        nome: `${lead.nome || ''} ${lead.cognome || ''}`.trim(),
        tipo_incidente: lead.tipo_incidente || '',
        category: lead.category || '',
        score: lead.score ?? 0,
        stato: lead.stato || '',
        gravita: lead.gravita || '',
        email: lead.email || '',
        telefono: lead.telefono || '',
        created_at: lead.created_at || '',
      }));
      const headers = ['nome', 'tipo_incidente', 'category', 'score', 'stato', 'gravita', 'email', 'telefono', 'created_at'];
      const csv = [
        headers.join(','),
        ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leadlegale_filtrati_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } finally {
      setIsCsvExporting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    setIsSavingPassword(true);
    try {
      const { data } = await axios.post(
        apiUrl('/api/change-password'),
        {
          old_password: oldPassword,
          new_password: newPassword,
          confirm_new_password: confirmNewPassword,
        },
        { headers: buildAuthHeaders() }
      );
      setPasswordMsg(data?.message || 'Password aggiornata correttamente.');
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      window.setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 1300);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPasswordMsg(typeof detail === 'string' ? detail : 'Errore durante il cambio password');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (stato) => {
    const labels = {
      nuovo: 'Nuovo',
      in_contatto: 'In Contatto',
      in_attesa_doc: 'In Attesa Doc',
      pratica_aperta: 'Pratica Aperta',
      contestato: 'Contestato',
      declinato: 'Declinato',
      non_fattibile_chiuso: 'Non Fattibile / Chiuso',
    };
    const classes = {
      nuovo: 'bg-blue-500 text-white font-semibold',
      in_contatto: 'bg-amber-400 text-slate-900 font-semibold',
      in_attesa_doc: 'bg-violet-500 text-white font-semibold',
      pratica_aperta: 'bg-emerald-500 text-white font-semibold',
      contestato: 'bg-orange-500 text-white font-semibold',
      declinato: 'bg-rose-700 text-white font-semibold',
      non_fattibile_chiuso: 'bg-slate-500 text-white font-semibold',
    };
    return (
      <Badge 
        className={`px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full ${classes[stato] || classes.nuovo}`}
        data-testid={`badge-stato-${stato}`}
      >
        {labels[stato] || labels.nuovo}
      </Badge>
    );
  };

  const getGravitaBadge = (gravita) => {
    const classes = {
      alta: 'badge-alta',
      media: 'badge-media',
      bassa: 'badge-bassa'
    };
    return (
      <Badge 
        className={`px-3 py-1 text-xs font-medium rounded-full ${classes[gravita] || classes.media}`}
        data-testid={`badge-gravita-${gravita}`}
      >
        {gravita.charAt(0).toUpperCase() + gravita.slice(1)}
      </Badge>
    );
  };

  const getCategoryBadge = (category) => {
    const labels = {
      ORO: 'ORO',
      ARGENTO: 'ARGENTO',
      BRONZO: 'BRONZO',
    };
    const classes = {
      ORO: 'bg-yellow-400 text-black font-bold',
      ARGENTO: 'bg-sky-200 text-slate-900 font-semibold',
      BRONZO: 'bg-slate-700 text-slate-100',
    };
    const normalized = String(category || '').toUpperCase();
    return (
      <Badge className={`px-3 py-1 text-xs font-semibold rounded-full ${classes[normalized] || classes.BRONZO}`}>
        {labels[normalized] || labels.BRONZO}
      </Badge>
    );
  };

  const formatResponsabilita = (value) => {
    const map = {
      ragione_piena: 'Ragione piena',
      concorso_colpa: 'Concorso colpa',
      colpa_propria: 'Colpa propria',
      non_so: 'Non so',
    };
    return map[value] || 'Non indicato';
  };

  const formatSiNo = (value) => {
    if (value === 'si') return 'Sì';
    if (value === 'no') return 'No';
    return 'Non indicato';
  };

  const openLeadDetails = (lead) => {
    setSelectedLead(lead);
    setIsDetailsOpen(true);
  };

  const copyLeadForWhatsapp = async (lead) => {
    const tipo = lead.tipo_incidente === 'malasanita' ? 'Malasanità' : 'Incidente';
    const notes = lead.descrizione || 'Nessuna nota';
    const text = `NUOVO LEAD LEADLEGALE\nNome: ${lead.nome} ${lead.cognome}\nTipo: ${tipo}\nTel: ${lead.telefono}\nNote: ${notes}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLeadId(lead.id);
      window.setTimeout(() => setCopiedLeadId(''), 1800);
    } catch (e) {
      console.error('Clipboard copy failed', e);
    }
  };

  const statusSelectStyle = (value) => {
    if (value === 'in_contatto') return { backgroundColor: '#FBBF24', color: '#111827', fontWeight: 600 };
    if (value === 'in_attesa_doc') return { backgroundColor: '#8B5CF6', color: '#FFFFFF', fontWeight: 600 };
    if (value === 'pratica_aperta') return { backgroundColor: '#10B981', color: '#FFFFFF', fontWeight: 600 };
    if (value === 'contestato') return { backgroundColor: '#F97316', color: '#FFFFFF', fontWeight: 600 };
    if (value === 'declinato') return { backgroundColor: '#BE123C', color: '#FFFFFF', fontWeight: 600 };
    if (value === 'non_fattibile_chiuso') return { backgroundColor: '#64748B', color: '#FFFFFF', fontWeight: 600 };
    return { backgroundColor: '#3B82F6', color: '#FFFFFF', fontWeight: 600 };
  };

  const statusFilterButtonStyle = (value, active) => {
    const palette = {
      tutti: { bg: '#334155', text: '#E2E8F0' },
      nuovo: { bg: '#3B82F6', text: '#FFFFFF' },
      in_contatto: { bg: '#F59E0B', text: '#111827' },
      in_attesa_doc: { bg: '#8B5CF6', text: '#FFFFFF' },
      pratica_aperta: { bg: '#10B981', text: '#FFFFFF' },
      contestato: { bg: '#F97316', text: '#FFFFFF' },
      declinato: { bg: '#BE123C', text: '#FFFFFF' },
      non_fattibile_chiuso: { bg: '#64748B', text: '#FFFFFF' },
    };
    const p = palette[value] || palette.tutti;
    if (active) {
      return { backgroundColor: p.bg, color: p.text, border: '1px solid rgba(255,255,255,0.25)' };
    }
    return { backgroundColor: '#112240', color: '#A0ABC0', border: '1px solid rgba(255,255,255,0.10)' };
  };

  const roleScopedLeads = leads.filter((lead) => {
    if (isLawyerLike) return isAssignedToCurrentLawyer(lead);
    return true;
  });
  const statusCounts = {
    nuovo: roleScopedLeads.filter((l) => l.stato === 'nuovo').length,
    in_contatto: roleScopedLeads.filter((l) => l.stato === 'in_contatto').length,
    in_attesa_doc: roleScopedLeads.filter((l) => l.stato === 'in_attesa_doc').length,
    pratica_aperta: roleScopedLeads.filter((l) => l.stato === 'pratica_aperta').length,
    contestato: roleScopedLeads.filter((l) => l.stato === 'contestato').length,
    declinato: roleScopedLeads.filter((l) => l.stato === 'declinato').length,
    non_fattibile_chiuso: roleScopedLeads.filter((l) => l.stato === 'non_fattibile_chiuso').length,
  };

  const getLeadDocuments = (lead) => {
    const files = Array.isArray(lead?.documenti_files) ? lead.documenti_files : [];
    const names = Array.isArray(lead?.documenti) ? lead.documenti : [];
    return files.map((stored, idx) => ({
      stored,
      label: names[idx] || stored,
      url: apiUrl(`/uploads/${encodeURIComponent(stored)}`),
    }));
  };

  const hasFreshGoldLead = allLeadsStats.some((lead) => {
    const isGold = String(lead?.category || '').toUpperCase() === 'ORO';
    const ts = Date.parse(lead?.created_at || '');
    if (!isGold || !Number.isFinite(ts)) return false;
    return Date.now() - ts <= 24 * 60 * 60 * 1000;
  });

  const totalLeadCount = allLeadsStats.length;
  const potentialLeadCount = allLeadsStats.filter((lead) => ['ORO', 'ARGENTO'].includes(String(lead?.category || '').toUpperCase())).length;
  const docsLeadCount = allLeadsStats.filter((lead) => (lead?.documenti_files?.length || lead?.documenti?.length || 0) > 0).length;
  const docsPercent = totalLeadCount > 0 ? Math.round((docsLeadCount / totalLeadCount) * 100) : 0;
  const adminTotalCredits = platformStats?.total_active_credits
    ?? lawyers.reduce((sum, lawyer) => sum + Number(lawyer?.credits ?? 0), 0);
  const adminActiveLawyersCount = platformStats?.active_lawyers_count
    ?? lawyers.filter((lawyer) => lawyer?.is_active !== false).length;
  const adminTotalLawyersCount = platformStats?.total_lawyers_count ?? lawyers.length;
  const adminTotalRevenue = platformStats?.total_revenue_historical ?? 0;
  const adminRecentLeads = (platformStats?.recent_leads?.length
    ? platformStats.recent_leads
    : sortLeadsByPriority(allLeadsStats).slice(0, 6).map((lead) => ({
        id: lead.id,
        nome: lead.nome,
        cognome: lead.cognome,
        tipo_incidente: lead.tipo_incidente,
        category: lead.category,
        stato: lead.stato,
        created_at: lead.created_at,
      })));

  const openRecentLead = (recentLead) => {
    const full = allLeadsStats.find((lead) => String(lead.id) === String(recentLead.id));
    if (full) {
      openLeadDetails(full);
      return;
    }
    openLeadDetails({
      id: recentLead.id,
      nome: recentLead.nome,
      cognome: recentLead.cognome,
      tipo_incidente: recentLead.tipo_incidente,
      category: recentLead.category,
      stato: recentLead.stato,
      created_at: recentLead.created_at,
      telefono: '',
      email: '',
      data_sinistro: '',
      luogo: '',
      descrizione: '',
      replay: [],
    });
  };

  const renderAdminNav = (testIdPrefix = 'nav-admin') => (
    ADMIN_SECTIONS.map(({ id, label, icon: Icon }) => (
      <button
        key={id}
        type="button"
        onClick={() => handleAdminSectionChange(id)}
        className={`sidebar-link w-full text-left ${adminSection === id ? 'active' : ''}`}
        data-testid={`${testIdPrefix}-${id}`}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span>{label}</span>
      </button>
    ))
  );

  const renderLawyerNav = (testIdPrefix = 'nav-lawyer') => (
    <>
      <button
        type="button"
        onClick={() => handleLawyerViewChange('leads')}
        className={`sidebar-link w-full text-left ${lawyerView === 'leads' ? 'active' : ''}`}
        data-testid={`${testIdPrefix}-leads`}
      >
        <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
        <span>Dashboard</span>
      </button>
      <button
        type="button"
        onClick={() => handleLawyerViewChange('calculator')}
        className={`sidebar-link w-full text-left ${lawyerView === 'calculator' ? 'active' : ''}`}
        data-testid={`${testIdPrefix}-calculator`}
      >
        <HeartPulse className="w-5 h-5" strokeWidth={1.5} />
        <span>Danno Biologico</span>
      </button>
    </>
  );

  const showCalculator = (isAdmin && adminSection === 'calculator') || (!isAdmin && lawyerView === 'calculator');
  const showLeadsWorkspace = (!isAdmin && lawyerView !== 'calculator') || (isAdmin && adminSection === 'leads');

  return (
    <div className="min-h-screen md:flex" style={{ backgroundColor: '#0A192F' }}>
      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex md:w-64 md:fixed md:left-0 md:top-0 md:bottom-0 flex-col" style={{ backgroundColor: '#1e293b' }}>
        {/* Logo */}
        <div className="p-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <Link to="/" className="flex items-center gap-2">
            <Scale className="w-8 h-8" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <span className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>LeadLegale</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {isAdmin ? renderAdminNav('nav-admin') : renderLawyerNav('nav-lawyer')}
        </nav>

        {isLawyerLike && (
          <div className="px-4 pb-2">
            <CreditsWalletCard {...walletCardProps} />
          </div>
        )}

        {/* User Info & Logout */}
        <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="mb-4 px-3">
            <p className="text-sm font-medium" style={{ color: '#F8F9FA' }}>{user?.name || 'Admin'}</p>
            <p className="text-xs" style={{ color: '#A0ABC0' }}>{user?.email}</p>
          </div>
          <button
            onClick={() => {
              setPasswordMsg('');
              setIsSecurityOpen(true);
            }}
            className="sidebar-link w-full text-left"
            data-testid="security-btn"
          >
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
            <span>Sicurezza</span>
          </button>
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-left"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span>Esci</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar (Hamburger Drawer) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(2, 6, 23, 0.65)' }}
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Chiudi menu"
          />
          <aside
            className="relative h-full w-72 flex flex-col"
            style={{ backgroundColor: '#1e293b', borderRight: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Scale className="w-7 h-7" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                <span className="text-base font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>LeadLegale</span>
              </Link>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-md" style={{ color: '#E2E8F0' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-1">
              {isAdmin ? renderAdminNav('nav-admin-mobile') : renderLawyerNav('nav-lawyer-mobile')}
            </nav>
            <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              {isLawyerLike && (
                <CreditsWalletCard
                  {...walletCardProps}
                  onManagePlan={() => {
                    setIsPlanModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="mb-3"
                />
              )}
              <div className="mb-4 px-3">
                <p className="text-sm font-medium" style={{ color: '#F8F9FA' }}>{user?.name || 'Admin'}</p>
                <p className="text-xs" style={{ color: '#A0ABC0' }}>{user?.email}</p>
              </div>
              <button
                onClick={() => {
                  setPasswordMsg('');
                  setIsSecurityOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="sidebar-link w-full text-left"
                data-testid="security-btn-mobile"
              >
                <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
                <span>Sicurezza</span>
              </button>
              <button
                onClick={handleLogout}
                className="sidebar-link w-full text-left"
                data-testid="logout-btn-mobile"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                <span>Esci</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-64 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 px-3 sm:px-4 md:px-8 py-3 md:py-6" style={{ backgroundColor: 'rgba(30, 41, 59, 0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <button
                className="md:hidden mt-0.5 p-2 rounded-md"
                style={{ backgroundColor: '#112240', color: '#E2E8F0' }}
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Apri menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <h1 className="text-lg sm:text-xl md:text-2xl font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                  {isAdmin
                    ? ADMIN_SECTION_TITLES[adminSection] || 'Panoramica'
                    : showCalculator
                      ? 'Calcolatore Danno Biologico'
                      : 'Gestione Lead'}
                </h1>
                {showLeadsWorkspace && (
                <div
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 shrink-0"
                  style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.35)' }}
                >
                  <span className="text-xs sm:text-sm font-semibold whitespace-nowrap" style={{ color: '#F8F9FA' }}>
                    Modalità Rapida
                  </span>
                  <Switch
                    checked={quickMode}
                    onCheckedChange={handleQuickModeChange}
                    className="data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-slate-600"
                    aria-label="Attiva o disattiva la modalità rapida"
                    data-testid="dashboard-quick-mode"
                  />
                </div>
                )}
              </div>
              {isLawyerLike && !showCalculator ? (
                <p className="text-xs sm:text-sm mt-1" style={{ color: '#A0ABC0' }}>
                  Area Riservata - {user?.name || (effectiveRole === 'partner' ? 'Partner' : 'Avvocato')}
                </p>
              ) : showCalculator ? (
                <p className="text-xs sm:text-sm mt-1" style={{ color: '#A0ABC0' }}>
                  Tabelle Milano 2024 — stima permanente, morale e inabilità temporanea
                </p>
              ) : isAdmin ? (
                <p className="text-xs sm:text-sm mt-1" style={{ color: '#A0ABC0' }}>
                  {adminSection === 'overview' && 'Totali piattaforma e ultimi lead in arrivo'}
                  {adminSection === 'analytics' && 'Business Intelligence — grafici aggiornati in tempo reale'}
                  {adminSection === 'financial' && 'Resoconto fiscale mensile per avvocato (solo auto-assign)'}
                  {adminSection === 'lawyers' && 'Partner, piani e ricariche manuali'}
                  {adminSection === 'leads' && `${filteredLeads.length} lead ${activeFilter !== 'tutti' ? `(${activeFilter})` : 'totali'}`}
                </p>
              ) : (
                <p className="text-xs sm:text-sm mt-1" style={{ color: '#A0ABC0' }}>
                  {filteredLeads.length} lead {activeFilter !== 'tutti' ? `(${activeFilter})` : 'totali'}
                </p>
              )}
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Button
                onClick={() => {
                  fetchLeads();
                  if (isAdmin) {
                    fetchPlatformStats();
                    if (adminSection === 'analytics') fetchAnalytics();
                    if (adminSection === 'financial') fetchFinancialSummary();
                  }
                }}
                variant="ghost"
                className="flex items-center gap-2 px-2 md:px-3"
                data-testid="refresh-leads-btn"
                style={{ color: '#A0ABC0' }}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                <span className="hidden sm:inline">Aggiorna</span>
              </Button>
              {showLeadsWorkspace && (
              <Button
                onClick={handleExport}
                disabled={isExporting || filteredLeads.length === 0}
                className="btn-secondary items-center gap-2 hidden md:flex"
                data-testid="export-excel-btn"
              >
                <Download className="w-4 h-4" strokeWidth={1.5} />
                {isExporting ? 'Esportazione...' : 'Esporta Excel'}
              </Button>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-3 sm:p-4 md:p-8">
          {isLawyerLike && (
            <div className="hidden md:block mb-6">
              <CreditsWalletCard {...walletCardProps} layout="wide" />
            </div>
          )}

          {isLawyerLike && (
            <div className="md:hidden mb-4">
              <CreditsWalletCard {...walletCardProps} />
            </div>
          )}

          {isAdmin && adminSection === 'overview' && (
            <>
              <AdminFinancialCards
                totalCredits={adminTotalCredits}
                activeLawyers={adminActiveLawyersCount}
                totalLawyers={adminTotalLawyersCount}
                totalRevenue={adminTotalRevenue}
              />
              <div
                className="rounded-lg p-4 md:p-5"
                style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="admin-recent-leads"
              >
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}>
                      Ultimi lead arrivati
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#A0ABC0' }}>
                      Panoramica rapida delle richieste più recenti
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleAdminSectionChange('leads')}
                    className="text-xs"
                    style={{ backgroundColor: '#1e293b', color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.35)' }}
                  >
                    Vedi archivio completo
                  </Button>
                </div>
                {adminRecentLeads.length === 0 ? (
                  <p className="text-sm py-6 text-center" style={{ color: '#A0ABC0' }}>Nessun lead registrato</p>
                ) : (
                  <div className="space-y-2">
                    {adminRecentLeads.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => openRecentLead(lead)}
                        className="w-full flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-3 text-left transition-colors hover:bg-slate-800/50"
                        style={{ backgroundColor: 'rgba(10, 25, 47, 0.55)', border: '1px solid rgba(255,255,255,0.06)' }}
                      >
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#F8F9FA' }}>
                            {lead.nome} {lead.cognome}
                          </p>
                          <p className="text-xs mt-0.5 capitalize" style={{ color: '#A0ABC0' }}>
                            {lead.tipo_incidente || 'stradale'} · {lead.stato || 'nuovo'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getCategoryBadge(lead.category)}
                          <span className="text-xs" style={{ color: '#64748B' }}>
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '—'}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {isAdmin && adminSection === 'analytics' && (
            <AdminAnalyticsPanel
              data={analyticsData}
              isLoading={isLoadingAnalytics}
              generatedAt={analyticsData?.generated_at}
            />
          )}

          {isAdmin && adminSection === 'financial' && (
            <AdminFinancialSummaryPanel
              data={financialData}
              isLoading={isLoadingFinancial}
              generatedAt={financialData?.generated_at}
            />
          )}

          {isAdmin && adminSection === 'lawyers' && (
            <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-sm font-semibold mb-1" style={{ color: '#F8F9FA' }}>Gestione Partner & Portafogli</p>
              <p className="text-xs mb-3" style={{ color: '#A0ABC0' }}>Piani attivi, saldi crediti e ricariche manuali per ogni avvocato.</p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <Input
                  type="text"
                  placeholder="Nome completo"
                  value={newLawyerName}
                  onChange={(e) => setNewLawyerName(e.target.value)}
                  style={{ backgroundColor: '#0A192F', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
                />
                <Input
                  type="email"
                  placeholder="email@studio.it"
                  value={newLawyerEmail}
                  onChange={(e) => setNewLawyerEmail(e.target.value)}
                  style={{ backgroundColor: '#0A192F', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
                />
                <Input
                  type="text"
                  placeholder="Password iniziale"
                  value={newLawyerPassword}
                  onChange={(e) => setNewLawyerPassword(e.target.value)}
                  style={{ backgroundColor: '#0A192F', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
                />
                <select
                  value={newLawyerCategory}
                  onChange={(e) => setNewLawyerCategory(e.target.value)}
                  className="rounded-md px-3 py-2 text-sm"
                  style={{ backgroundColor: '#0A192F', border: '1px solid rgba(255,255,255,0.1)', color: '#F8F9FA' }}
                >
                  <option value="all">Tutte le categorie</option>
                  <option value="stradale">Solo Stradale</option>
                  <option value="malasanita">Solo Malasanità</option>
                </select>
                <Button
                  type="button"
                  onClick={createLawyer}
                  disabled={isCreatingLawyer || !newLawyerName.trim() || !newLawyerEmail.trim() || !newLawyerPassword}
                  className="btn-primary"
                  style={{ backgroundColor: '#1e293b', color: '#ffffff' }}
                >
                  {isCreatingLawyer ? 'Salvataggio...' : 'Aggiungi'}
                </Button>
              </div>
              <p className="text-xs mb-3" style={{ color: lawyerMsg.includes('successo') || lawyerMsg.includes('eliminato') ? '#22C55E' : '#A0ABC0' }}>{lawyerMsg || 'Crea nuovi avvocati e assegnali ai lead dalla tabella.'}</p>
              <div className="rounded-lg overflow-hidden overflow-x-auto" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                <Table data-testid="lawyers-commercial-table">
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#0A192F' }}>
                      <TableHead style={{ color: '#A0ABC0' }}>Avvocato</TableHead>
                      <TableHead style={{ color: '#A0ABC0' }}>Piano</TableHead>
                      <TableHead style={{ color: '#A0ABC0' }}>Crediti</TableHead>
                      <TableHead style={{ color: '#A0ABC0' }}>Lead inclusi</TableHead>
                      <TableHead style={{ color: '#A0ABC0' }}>Stato Lead Assegnati</TableHead>
                      <TableHead style={{ color: '#A0ABC0' }}>Stato</TableHead>
                      <TableHead style={{ color: '#A0ABC0' }}>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lawyers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6" style={{ color: '#A0ABC0' }}>
                          Nessun partner registrato
                        </TableCell>
                      </TableRow>
                    ) : (
                      lawyers.map((lawyer) => (
                        <TableRow key={lawyer.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm" style={{ color: '#F8F9FA' }}>{lawyer.name || lawyer.username}</p>
                              <p className="text-xs" style={{ color: '#A0ABC0' }}>{lawyer.email}</p>
                              <p className="text-[10px] mt-0.5" style={{ color: '#64748B' }}>{lawyer.practice_category || 'all'} · {lawyer.role || 'partner'}</p>
                            </div>
                          </TableCell>
                          <TableCell style={{ color: '#F8F9FA' }}>{PLAN_LABELS[lawyer.subscription_plan] || lawyer.subscription_plan || 'Starter'}</TableCell>
                          <TableCell style={{ color: '#D4AF37', fontWeight: 600 }}>{Number(lawyer.credits ?? 0).toFixed(2)} €</TableCell>
                          <TableCell style={{ color: '#F8F9FA' }}>{Number(lawyer.leads_included_remaining ?? 0)}</TableCell>
                          <TableCell>
                            <LawyerAssignedLeadStatus lawyer={lawyer} />
                          </TableCell>
                          <TableCell>
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                              style={{
                                backgroundColor: lawyer.is_active !== false ? '#10B981' : '#EF4444',
                                color: '#fff',
                              }}
                            >
                              {lawyer.is_active !== false ? 'Attivo' : 'Sospeso'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => openAdminTopup(lawyer)}
                                className="text-xs"
                                style={{ backgroundColor: '#2563EB', color: '#fff' }}
                                data-testid={`topup-lawyer-${lawyer.id}`}
                              >
                                Ricarica Manuale
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => openResetLawyerPassword(lawyer)}
                                className="text-xs"
                                style={{ backgroundColor: '#1e293b', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.45)' }}
                                data-testid={`reset-password-lawyer-${lawyer.id}`}
                              >
                                <KeyRound className="w-3.5 h-3.5 mr-1" />
                                Reset Password
                              </Button>
                              <button
                                type="button"
                                onClick={() => deleteLawyer(lawyer)}
                                disabled={deletingLawyerId === String(lawyer.id)}
                                className="inline-flex items-center justify-center rounded p-1.5 transition-opacity hover:opacity-80 disabled:opacity-40"
                                style={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.35)' }}
                                title="Elimina avvocato"
                              >
                                <X className="w-4 h-4" strokeWidth={2.5} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {showCalculator && <DannoBiologicoCalculator />}

          {(!isAdmin || adminSection === 'leads') && showLeadsWorkspace && (
          <>
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
            <div className="rounded-lg p-3 md:p-4" style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>Totale Lead</p>
              <p className="text-lg md:text-2xl font-semibold mt-1" style={{ color: '#F8F9FA' }}>{totalLeadCount}</p>
            </div>
            <div className="rounded-lg p-3 md:p-4" style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>Valore Potenziale</p>
              <p className="text-lg md:text-2xl font-semibold mt-1" style={{ color: '#F8F9FA' }}>{potentialLeadCount}</p>
              <p className="text-[10px] md:text-xs mt-1" style={{ color: '#D4AF37' }}>ORO + ARGENTO</p>
            </div>
            <div className="rounded-lg p-3 md:p-4" style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>Documentazione</p>
              <p className="text-lg md:text-2xl font-semibold mt-1" style={{ color: '#F8F9FA' }}>{docsPercent}%</p>
              <p className="text-[10px] md:text-xs mt-1" style={{ color: '#A0ABC0' }}>{docsLeadCount}/{totalLeadCount} allegati</p>
            </div>
          </div>

          {/* Filters & Search */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            {/* Filter Tabs */}
            <div className="flex items-center gap-2 p-1 rounded-lg" style={{ backgroundColor: '#112240' }}>
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setActiveFilter(option.value)}
                  className={`filter-tab flex items-center gap-2 ${activeFilter === option.value ? 'active' : ''}`}
                  data-testid={`filter-${option.value}`}
                  style={activeFilter === option.value ? { backgroundColor: '#1e293b', color: '#ffffff' } : {}}
                >
                  <option.icon className="w-4 h-4" strokeWidth={1.5} />
                  {option.label}
                </button>
              ))}
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 p-1 rounded-lg" style={{ backgroundColor: '#112240' }}>
              {[
                { value: 'tutti', label: 'Mostra Tutti' },
                { value: 'ORO', label: 'Solo ORO' },
                { value: 'ARGENTO', label: 'Solo ARGENTO' },
              ].map((item) => (
                <button
                  key={item.value}
                  onClick={() => setCategoryFilter(item.value)}
                  className="filter-tab"
                  data-testid={`filter-category-${item.value}`}
                  style={categoryFilter === item.value ? { backgroundColor: '#1e293b', color: '#ffffff' } : {}}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {/* Status Quick Filter */}
            <div className="flex items-center gap-2 p-1 rounded-lg" style={{ backgroundColor: '#112240' }}>
              {STATUS_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  onClick={() => setStatusFilter(item.value)}
                  className="filter-tab"
                  data-testid={`filter-status-${item.value}`}
                  style={statusFilterButtonStyle(item.value, statusFilter === item.value)}
                >
                  {item.value === 'tutti'
                    ? `${item.label} (${roleScopedLeads.length})`
                    : `${item.label} (${statusCounts[item.value] || 0})`}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#A0ABC0' }} strokeWidth={1.5} />
              <Input
                type="text"
                placeholder="Cerca lead..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full sm:w-64 input-luxury"
                data-testid="search-leads-input"
                style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              />
            </div>

            <Button
              onClick={handleCsvExport}
              disabled={isCsvExporting || filteredLeads.length === 0}
              className="btn-secondary"
              data-testid="export-csv-btn"
            >
              {isCsvExporting ? 'Esportazione CSV...' : 'Scarica CSV'}
            </Button>
          </div>

          {leadActionMsg && (
            <p
              className="text-sm mb-4 px-3 py-2 rounded-lg"
              style={{
                color: leadActionMsg.includes('successo') ? '#22C55E' : '#FCA5A5',
                backgroundColor: 'rgba(17, 34, 64, 0.85)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              role="alert"
            >
              {leadActionMsg}
            </p>
          )}

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 mb-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-4" style={{ color: '#A0ABC0' }} strokeWidth={1} />
                <p style={{ color: '#A0ABC0' }}>Nessun lead trovato</p>
                {leadsDebugMsg && (
                  <p className="text-xs mt-2 px-4" style={{ color: '#FCD34D' }}>
                    {leadsDebugMsg}
                  </p>
                )}
              </div>
            ) : (
              filteredLeads.map((lead) => (
                <LeadCard
                  key={`mobile-${lead.id}`}
                  lead={lead}
                  onOpenDetails={openLeadDetails}
                  renderStatusBadge={getStatusBadge}
                  renderCategoryBadge={getCategoryBadge}
                  isAssignedToCurrentLawyer={isAssignedToCurrentLawyer}
                  quickMode={quickMode}
                  canContest={canContestLead(lead)}
                  onContestLead={openDisputeLead}
                  canDecline={canDeclineAssignedLead(lead)}
                  onDeclineLead={openDeclineLead}
                />
              ))
            )}
          </div>

          {/* Leads Table (Desktop) */}
          <div className="hidden md:block rounded-lg overflow-hidden overflow-x-auto -mx-1 sm:mx-0" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-12 h-12 mx-auto mb-4" style={{ color: '#A0ABC0' }} strokeWidth={1} />
                <p style={{ color: '#A0ABC0' }}>Nessun lead trovato</p>
                {leadsDebugMsg && (
                  <p className="text-xs mt-2 px-4" style={{ color: '#FCD34D' }}>
                    {leadsDebugMsg}
                  </p>
                )}
              </div>
            ) : quickMode ? (
              <Table data-testid="leads-table-quick">
                <TableHeader>
                  <TableRow style={{ backgroundColor: '#112240' }}>
                    <TableHead style={{ color: '#A0ABC0' }}>Lead</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Contatti rapidi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => {
                    const scoreVal = Number.isFinite(lead.score) ? lead.score : 0;
                    return (
                      <TableRow
                        key={lead.id}
                        data-testid={`lead-row-quick-${lead.id}`}
                        className="transition-colors hover:bg-slate-800/40"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                      >
                        <TableCell className="py-4 align-middle">
                          <div className="flex items-center gap-3">
                            <div className={`score-badge shrink-0 ${scoreVal >= 80 ? 'score-top' : 'score-normal'}`}>
                              {scoreVal}
                            </div>
                            <button
                              type="button"
                              className="text-left font-semibold text-lg leading-tight hover:underline"
                              style={{ color: '#F8F9FA' }}
                              onClick={() => openLeadDetails(lead)}
                            >
                              {lead.nome} {lead.cognome}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell className="py-4 align-middle">
                          <div className="flex flex-wrap items-center gap-3">
                            <a
                              href={`tel:${lead.telefono || ''}`}
                              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-base font-semibold shadow-md min-w-[120px]"
                              style={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}
                            >
                              <Phone className="w-5 h-5" />
                              Chiama
                            </a>
                            <a
                              href={lead.email ? `mailto:${lead.email}` : '#'}
                              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-base font-semibold min-w-[120px] border-2"
                              style={{
                                backgroundColor: 'rgba(10, 25, 47, 0.55)',
                                borderColor: '#D4AF37',
                                color: '#F8F9FA',
                              }}
                            >
                              <Mail className="w-5 h-5" style={{ color: '#D4AF37' }} />
                              Email
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow style={{ backgroundColor: '#112240' }}>
                    <TableHead style={{ color: '#A0ABC0' }}>Nome</TableHead>
                    <TableHead className="hidden sm:table-cell" style={{ color: '#A0ABC0' }}>Data Sinistro</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Tipo Danno</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Categoria</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Stato</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Assegna Avvocato</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Gravità</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Dettagli</TableHead>
                    <TableHead style={{ color: '#A0ABC0' }}>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow 
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      className="transition-colors hover:bg-slate-800/40"
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <TableCell className="py-3 md:py-4">
                        <div>
                          <div className="lead-row" style={{ cursor: 'default', borderBottom: 'none', padding: '0.5rem 0' }}>
                            <div className={`score-badge ${(Number.isFinite(lead.score) ? lead.score : 0) >= 80 ? 'score-top' : 'score-normal'}`}>
                              {Number.isFinite(lead.score) ? lead.score : 0}
                            </div>

                            <div style={{ flexGrow: 1 }}>
                              <div style={{ fontSize: '16px', fontWeight: '600', color: '#F8F9FA' }} className="flex items-center gap-2 flex-wrap">
                                {lead.nome} {lead.cognome}
                                {String(lead.category || '').toUpperCase() === 'ORO' && Date.now() - (Date.parse(lead.created_at || '') || 0) <= 24 * 60 * 60 * 1000 && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#EF4444', color: '#fff' }}>
                                    NEW
                                  </span>
                                )}
                              </div>
                              <div className="lead-mini-info">
                                {lead.gravita || ''}
                              </div>
                            </div>

                            <div style={{ color: '#A0ABC0', fontSize: '12px' }}>
                              {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '—'}
                            </div>
                          </div>
                          {((lead.documenti_files?.length || 0) > 0 || (lead.documenti?.length || 0) > 0) && (
                            <div className="text-xs mt-1" style={{ color: '#D4AF37' }}>
                              <a
                                href={getLeadDocuments(lead)[0]?.url || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 underline"
                                style={{ color: '#FCD34D' }}
                              >
                                <Paperclip className="w-3 h-3" />
                                Apri documento
                              </a>
                            </div>
                          )}
                          <p className="text-xs sm:text-sm break-all mt-1" style={{ color: '#A0ABC0' }}>{lead.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell py-3 md:py-4" style={{ color: '#F8F9FA' }}>{formatDate(lead.data_sinistro)}</TableCell>
                      <TableCell className="py-3 md:py-4">
                        <div className="flex items-center gap-2">
                          {lead.tipo_incidente === 'stradale' ? (
                            <Car className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                          ) : (
                            <Stethoscope className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                          )}
                          <span style={{ color: '#F8F9FA' }}>
                            {lead.tipo_incidente.charAt(0).toUpperCase() + lead.tipo_incidente.slice(1)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 md:py-4">{getCategoryBadge(lead.category)}</TableCell>
                      <TableCell className="py-3 md:py-4">
                        <div className="space-y-2">
                          <select
                            value={lead.stato || 'nuovo'}
                            onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                            className="text-[11px] sm:text-xs rounded-md px-2 py-1 border-0 outline-none"
                            style={statusSelectStyle(lead.stato || 'nuovo')}
                            data-testid={`lead-status-${lead.id}`}
                            disabled={lead.stato === 'contestato' || lead.stato === 'declinato'}
                          >
                            <option value="nuovo">Nuovo</option>
                            <option value="in_contatto">In Contatto</option>
                            <option value="in_attesa_doc">In Attesa Doc</option>
                            <option value="pratica_aperta">Pratica Aperta</option>
                            <option value="contestato">Contestato</option>
                            <option value="declinato">Declinato</option>
                            <option value="non_fattibile_chiuso">Non Fattibile / Chiuso</option>
                          </select>
                          {getStatusBadge(lead.stato || 'nuovo')}
                        </div>
                      </TableCell>
                      <TableCell className="py-3 md:py-4">
                        {effectiveRole === 'admin' ? (
                          <div className="space-y-1">
                            <select
                              value={lead.assigned_to || ''}
                              onChange={(e) => assignLead(lead.id, e.target.value)}
                              disabled={assigningLeadId === lead.id}
                              className="text-[11px] sm:text-xs rounded-md px-2 py-1 border-0 outline-none"
                              style={{ backgroundColor: '#334155', color: '#E2E8F0' }}
                              data-testid={`lead-assign-${lead.id}`}
                            >
                              <option value="">Non assegnato</option>
                              {lawyers.map((lawyer) => (
                                <option key={lawyer.id} value={lawyer.id}>
                                  {(lawyer.name || lawyer.username)} ({lawyer.role || 'partner'})
                                </option>
                              ))}
                            </select>
                            <p className="text-[11px]" style={{ color: '#A0ABC0' }}>
                              Assegnato: {getLawyerNameById(lead.assigned_to)}
                            </p>
                            {assignmentSavedLeadId === lead.id && (
                              <p className="text-[11px]" style={{ color: '#22C55E' }}>
                                Assegnazione salvata
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs" style={{ color: '#A0ABC0' }}>
                            {isAssignedToCurrentLawyer(lead) ? 'Assegnato a te' : 'Non assegnato'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-3 md:py-4">{getGravitaBadge(lead.gravita)}</TableCell>
                      <TableCell className="py-3 md:py-4">
                        <Button
                          variant="ghost"
                          className="text-sm"
                          style={{ color: '#D4AF37' }}
                          data-testid={`lead-details-${lead.id}`}
                          onClick={() => openLeadDetails(lead)}
                        >
                          Vedi Dettagli
                        </Button>
                      </TableCell>
                      <TableCell className="py-3 md:py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            className="text-xs"
                            style={{ color: '#D4AF37' }}
                            onClick={() => copyLeadForWhatsapp(lead)}
                            data-testid={`copy-lead-${lead.id}`}
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copia
                          </Button>
                          {canContestLead(lead) && (
                            <Button
                              variant="ghost"
                              className="text-xs"
                              style={{ color: '#F59E0B' }}
                              onClick={() => openDisputeLead(lead)}
                              data-testid={`contest-lead-${lead.id}`}
                            >
                              <Flag className="w-4 h-4 mr-1" />
                              Contesta Lead (Garanzia 24h)
                            </Button>
                          )}
                          {canDeclineAssignedLead(lead) && (
                            <Button
                              variant="ghost"
                              className="text-xs"
                              style={{ color: '#FCA5A5' }}
                              onClick={() => openDeclineLead(lead)}
                              data-testid={`decline-lead-${lead.id}`}
                            >
                              <MailX className="w-4 h-4 mr-1" />
                              Declina Pratica
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            className="text-xs"
                            style={{ color: '#EF4444' }}
                            onClick={() => deleteLead(lead.id)}
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Elimina
                          </Button>
                        </div>
                        {copiedLeadId === lead.id && (
                          <p className="text-xs mt-1" style={{ color: '#22C55E' }}>Copiato!</p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          </>
          )}
        </div>
      </main>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
          style={{ backgroundColor: '#0A192F', border: '1px solid rgba(212, 175, 55, 0.35)', color: '#F8F9FA' }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}>
              Dettaglio Lead
            </DialogTitle>
            <DialogDescription style={{ color: '#A0ABC0' }}>
              Risposte complete fornite dal potenziale cliente.
            </DialogDescription>
          </DialogHeader>

          {selectedLead && (
            <div className="overflow-y-auto px-6 pb-6 space-y-6 flex-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 rounded-md" style={{ backgroundColor: '#112240' }}>
                  <p className="text-xs uppercase tracking-wide" style={{ color: '#D4AF37' }}>Nome e Cognome</p>
                  <p style={{ color: '#F8F9FA' }}>{selectedLead.nome} {selectedLead.cognome}</p>
                </div>
                <div className="p-3 rounded-md" style={{ backgroundColor: '#112240' }}>
                  <p className="text-xs uppercase tracking-wide" style={{ color: '#D4AF37' }}>Contatti</p>
                  <p style={{ color: '#F8F9FA' }}>{selectedLead.telefono}</p>
                  <p style={{ color: '#A0ABC0' }}>{selectedLead.email}</p>
                </div>
                <div className="p-3 rounded-md" style={{ backgroundColor: '#112240' }}>
                  <p className="text-xs uppercase tracking-wide" style={{ color: '#D4AF37' }}>Sinistro</p>
                  <p style={{ color: '#F8F9FA' }}>{formatDate(selectedLead.data_sinistro)} - {selectedLead.luogo}</p>
                </div>
                <div className="p-3 rounded-md" style={{ backgroundColor: '#112240' }}>
                  <p className="text-xs uppercase tracking-wide" style={{ color: '#D4AF37' }}>Tipo e Gravità</p>
                  <p style={{ color: '#F8F9FA' }}>
                    {(selectedLead.tipo_incidente || '').charAt(0).toUpperCase() + (selectedLead.tipo_incidente || '').slice(1)} / {(selectedLead.gravita_danni || 'non indicata')}
                  </p>
                  <div className="mt-2">{getGravitaBadge(selectedLead.gravita || 'media')}</div>
                </div>
              </div>

              {canContestLead(selectedLead) && (
                <Button
                  type="button"
                  onClick={() => openDisputeLead(selectedLead)}
                  className="w-full"
                  style={{ backgroundColor: 'rgba(212, 175, 55, 0.15)', color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.4)' }}
                  data-testid="contest-lead-details"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Contesta Lead (Garanzia 24h)
                </Button>
              )}
              {canDeclineAssignedLead(selectedLead) && (
                <Button
                  type="button"
                  onClick={() => openDeclineLead(selectedLead)}
                  className="w-full"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.35)' }}
                  data-testid="decline-lead-details"
                >
                  <MailX className="w-4 h-4 mr-2" />
                  Declina Pratica
                </Button>
              )}

              <div className="p-4 rounded-md" style={{ backgroundColor: '#112240' }}>
                <p
                  className="text-xs uppercase tracking-wide mb-2"
                  style={{ color: '#D4AF37' }}
                >
                  Descrizione Incidente
                </p>
                <div
                  style={{ color: '#F8F9FA', whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{
                    __html: escapeHTMLWithBreaks(selectedLead.descrizione || 'Nessuna descrizione fornita.'),
                  }}
                />
              </div>

              <div className="p-4 rounded-md" style={{ backgroundColor: '#112240' }}>
                <p className="text-sm font-medium mb-3" style={{ color: '#D4AF37' }}>Dettagli Legali (Pagina 4)</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p style={{ color: '#A0ABC0' }}>Responsabilità</p>
                    <p style={{ color: '#F8F9FA' }}>{formatResponsabilita(selectedLead.responsabilita)}</p>
                  </div>
                  <div>
                    <p style={{ color: '#A0ABC0' }}>Referto Medico</p>
                    <p style={{ color: '#F8F9FA' }}>{formatSiNo(selectedLead.ha_referto_medico)}</p>
                  </div>
                  <div>
                    <p style={{ color: '#A0ABC0' }}>Autorità / CID</p>
                    <p style={{ color: '#F8F9FA' }}>{formatSiNo(selectedLead.autorita_intervenute)}</p>
                  </div>
                </div>
              </div>

              <LeadReplayHistory replay={selectedLead.replay} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <DisputeLeadModal
        open={Boolean(disputeLead)}
        lead={disputeLead}
        onOpenChange={(open) => {
          if (!open) {
            setDisputeLead(null);
            setDisputeError('');
          }
        }}
        onSubmit={submitLeadDispute}
        isSubmitting={isDisputing}
        error={disputeError}
      />
      <DeclineLeadModal
        open={Boolean(declineLead)}
        lead={declineLead}
        onOpenChange={(open) => {
          if (!open) {
            setDeclineLead(null);
            setDeclineError('');
          }
        }}
        onSubmit={submitLeadDecline}
        isSubmitting={isDeclining}
        error={declineError}
      />
      <Dialog open={isSecurityOpen} onOpenChange={setIsSecurityOpen}>
        <DialogContent
          className="max-w-md"
          style={{ backgroundColor: '#0A192F', border: '1px solid rgba(212, 175, 55, 0.35)', color: '#F8F9FA' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}>
              Sicurezza Account
            </DialogTitle>
            <DialogDescription style={{ color: '#A0ABC0' }}>
              Aggiorna la tua password per proteggere l&apos;accesso alla dashboard.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <Input
              type="password"
              placeholder="Vecchia Password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
            />
            <Input
              type="password"
              placeholder="Nuova Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
            />
            <Input
              type="password"
              placeholder="Conferma Nuova Password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
              style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
            />
            {passwordMsg && (
              <p className="text-xs" style={{ color: passwordMsg.toLowerCase().includes('corrett') ? '#22C55E' : '#F87171' }}>
                {passwordMsg}
              </p>
            )}
            <Button
              type="submit"
              disabled={isSavingPassword}
              className="w-full btn-primary"
              style={{ backgroundColor: '#1e293b', color: '#ffffff' }}
            >
              {isSavingPassword ? 'Salvataggio...' : 'Aggiorna Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isPlanModalOpen} onOpenChange={setIsPlanModalOpen}>
        <DialogContent
          className="max-w-md"
          style={{ backgroundColor: '#0A192F', border: '1px solid rgba(212, 175, 55, 0.35)', color: '#F8F9FA' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}>
              Gestione Piano & Crediti
            </DialogTitle>
            <DialogDescription style={{ color: '#A0ABC0' }}>
              Area temporanea: la ricarica online sarà disponibile a breve.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm" style={{ color: '#E2E8F0' }}>
            <p>
              Saldo attuale: <strong>{Number(user?.credits ?? 0).toFixed(2)} €</strong>
            </p>
            <p>
              Lead inclusi nel piano: <strong>{Number(user?.leads_included_remaining ?? 0)}</strong>
            </p>
            <p>
              Piano attivo: <strong>{PLAN_LABELS[user?.subscription_plan] || user?.subscription_plan || 'Starter'}</strong>
            </p>
            <p className="text-xs" style={{ color: '#A0ABC0' }}>
              Per ricaricare crediti o passare a Business/Pro, contatta l&apos;amministrazione LeadLegale.
              Ogni lead extra oltre il piano costa 50 € di credito.
            </p>
          </div>
          <Button
            type="button"
            className="w-full mt-2"
            style={{ backgroundColor: '#1e293b', color: '#ffffff' }}
            onClick={() => setIsPlanModalOpen(false)}
          >
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={isAdminTopupOpen} onOpenChange={setIsAdminTopupOpen}>
        <DialogContent
          className="max-w-md"
          style={{ backgroundColor: '#0A192F', border: '1px solid rgba(212, 175, 55, 0.35)', color: '#F8F9FA' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}>
              Ricarica Manuale
            </DialogTitle>
            <DialogDescription style={{ color: '#A0ABC0' }}>
              {topupLawyer ? `${topupLawyer.name || topupLawyer.username} · ${topupLawyer.email}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A0ABC0' }}>Importo crediti (€)</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Es. 150 oppure -50 per scalare"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A0ABC0' }}>Aggiorna piano (opzionale)</label>
              <select
                value={topupPlan}
                onChange={(e) => setTopupPlan(e.target.value)}
                className="w-full rounded-md px-3 py-2 text-sm"
                style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              >
                <option value="">— Nessun cambio piano —</option>
                <option value="starter">Starter (0 lead inclusi)</option>
                <option value="business">Business (4 lead inclusi)</option>
                <option value="pro">Pro (12 lead inclusi)</option>
              </select>
            </div>
            {topupMsg && (
              <p className="text-xs" style={{ color: topupMsg.includes('successo') ? '#22C55E' : '#F87171' }}>
                {topupMsg}
              </p>
            )}
            <Button
              type="button"
              disabled={isTopupSaving}
              onClick={submitAdminTopup}
              className="w-full"
              style={{ backgroundColor: '#2563EB', color: '#fff' }}
            >
              {isTopupSaving ? 'Salvataggio...' : 'Applica Ricarica'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(resetLawyer)}
        onOpenChange={(open) => {
          if (!open) {
            setResetLawyer(null);
            setResetPassword('');
            setResetPasswordConfirm('');
            setResetMsg('');
            setResetCopied(false);
          }
        }}
      >
        <DialogContent
          className="max-w-md"
          style={{ backgroundColor: '#0A192F', border: '1px solid rgba(212, 175, 55, 0.35)', color: '#F8F9FA' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}>
              Reset Password
            </DialogTitle>
            <DialogDescription style={{ color: '#A0ABC0' }}>
              {resetLawyer ? `${resetLawyer.name || resetLawyer.username} · ${resetLawyer.email}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs" style={{ color: '#A0ABC0' }}>
              Minimo 8 caratteri, una maiuscola, un numero e un simbolo (!@#$%^&*).
            </p>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A0ABC0' }}>Nuova password</label>
              <Input
                type="text"
                autoComplete="off"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                data-testid="reset-password-input"
                style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A0ABC0' }}>Conferma password</label>
              <Input
                type="text"
                autoComplete="off"
                value={resetPasswordConfirm}
                onChange={(e) => setResetPasswordConfirm(e.target.value)}
                data-testid="reset-password-confirm"
                style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                className="text-xs"
                onClick={fillGeneratedResetPassword}
                style={{ color: '#D4AF37' }}
                data-testid="reset-password-generate"
              >
                Genera casuale
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-xs"
                onClick={copyResetPassword}
                disabled={!resetPassword}
                style={{ color: resetCopied ? '#22C55E' : '#A0ABC0' }}
                data-testid="reset-password-copy"
              >
                <Copy className="w-3.5 h-3.5 mr-1" />
                {resetCopied ? 'Copiata' : 'Copia'}
              </Button>
            </div>
            {resetMsg && (
              <p
                className="text-xs"
                style={{ color: resetMsg.toLowerCase().includes('aggiornata') || resetMsg.toLowerCase().includes('generata') ? '#22C55E' : '#F87171' }}
              >
                {resetMsg}
              </p>
            )}
            <Button
              type="button"
              disabled={isResettingPassword}
              onClick={submitResetLawyerPassword}
              className="w-full"
              style={{ backgroundColor: '#D4AF37', color: '#0A192F' }}
              data-testid="reset-password-submit"
            >
              {isResettingPassword ? 'Salvataggio...' : 'Salva nuova password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DashboardPage;
